/**
 * Invite links and codes as members paste or scan them. A code is `randomInviteCode()` in
 * convex/functions/lib/crypto.ts: 12 random bytes, base64url, so exactly 16 URL-safe characters.
 */

export const INVITE_CODE_RE = /^[A-Za-z0-9_-]{16}$/;

/** The invite code in a pasted `…/join/<code>` link, or a bare code (8+ URL-safe characters). Lenient: the server decides. */
export function inviteCodeFrom(text: string): string | null {
  const trimmed = text.trim();
  return /\/join\/([A-Za-z0-9_-]+)/.exec(trimmed)?.[1] ?? (/^[A-Za-z0-9_-]{8,}$/.test(trimmed) ? trimmed : null);
}

/**
 * The invite code in a scanned QR payload, or null when it is not a Bolt Circles invite:
 * an `http(s)` link on `origin` whose path is exactly `/join/<code>`, or a bare code of the minted shape.
 * Stricter than `inviteCodeFrom`: a camera sees arbitrary codes, and a link to another site must not open here.
 */
export function scannedInviteCode(text: string, origin: string): string | null {
  const trimmed = text.trim();
  if (INVITE_CODE_RE.test(trimmed)) return trimmed;
  let url: URL;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const code = /^\/join\/([^/]+)\/?$/.exec(url.pathname)?.[1];
  return code && INVITE_CODE_RE.test(code) ? code : null;
}
